/**
 * MHR App Component
 *
 * Medical History Review - AI-powered pre-screening tool.
 * Integrated into SiteOperations.io - uses shared authentication.
 * Note: MHRProvider is in App.tsx to persist state across navigation.
 */

import { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { useMHR } from './contexts';
import { Layout } from './components/layout';
import { MHRErrorBoundary } from './components/ErrorBoundary';
import { Dashboard, PatientDatabase, PatientChart, SnapshotView, ProtocolSetup, CriteriaEditor, DocumentUpload, SummaryReview, ReviewResults, MultiStudyConsider, BudgetManagement, MHRSettings } from './pages';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

// Protected route wrapper - uses shared auth from main app
function ProtectedRoute({
  children,
  requireAdmin = false,
}: {
  children: React.ReactNode;
  requireAdmin?: boolean;
}) {
  const { session, user: authUser, currentSite } = useAuth();
  const { user, loading, isAdmin } = useMHR();
  const navigate = useNavigate();
  const [entitlementChecked, setEntitlementChecked] = useState(false);
  const [hasEntitlement, setHasEntitlement] = useState(false);
  const [checkingEntitlement, setCheckingEntitlement] = useState(true);

  // Check entitlement when component mounts or site changes
  useEffect(() => {
    const checkEntitlement = async () => {
      // Super admins always have access
      if (authUser?.role === 'super_admin') {
        setHasEntitlement(true);
        setEntitlementChecked(true);
        setCheckingEntitlement(false);
        return;
      }

      if (!currentSite) {
        setHasEntitlement(false);
        setEntitlementChecked(true);
        setCheckingEntitlement(false);
        return;
      }

      try {
        const response = await api.get('/api/my-tools');
        const tools = response.data || [];
        const mhrTool = tools.find((t: any) => t.tool_id === 'medical_history_review');
        setHasEntitlement(mhrTool?.is_available === true);
        setEntitlementChecked(true);
      } catch (error) {
        console.error('Error checking MHR entitlement:', error);
        setHasEntitlement(false);
        setEntitlementChecked(true);
      } finally {
        setCheckingEntitlement(false);
      }
    };

    if (session && !loading) {
      checkEntitlement();
    }
  }, [session, authUser?.role, currentSite, loading]);

  if (loading || checkingEntitlement) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 dark:border-primary-400"></div>
      </div>
    );
  }

  // If no session, redirect to main login
  if (!session) {
    return <Navigate to="/login" replace />;
  }

  // Check entitlement (super admins bypass this check)
  if (entitlementChecked && !hasEntitlement && authUser?.role !== 'super_admin') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center p-8 max-w-md">
          <div className="text-6xl mb-4">🔒</div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">Access Denied</h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            You do not have access to Medical History Review for your current site. Please contact
            your administrator if you believe this is an error.
          </p>
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-1.5 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[44px] sm:min-h-0"
          >
            Return to Tool Hub
          </button>
        </div>
      </div>
    );
  }

  // If admin required but user is not admin
  if (requireAdmin && !isAdmin && authUser?.role !== 'super_admin') {
    return <Navigate to="/mhr" replace />;
  }

  return <>{children}</>;
}

// MHR Routes
function MHRRoutes() {
  return (
    <Routes>
      {/* Patient Database (MedHx Builder landing page) */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout>
              <PatientDatabase />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Patient Chart */}
      <Route
        path="/patients/:patientId"
        element={
          <ProtectedRoute>
            <Layout>
              <PatientChart />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Snapshot View */}
      <Route
        path="/patients/:patientId/snapshots/:snapshotId"
        element={
          <ProtectedRoute>
            <Layout>
              <SnapshotView />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Protocol Dashboard */}
      <Route
        path="/protocols"
        element={
          <ProtectedRoute>
            <Layout>
              <Dashboard />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* New protocol */}
      <Route
        path="/protocols/new"
        element={
          <ProtectedRoute>
            <Layout>
              <ProtocolSetup />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Edit protocol */}
      <Route
        path="/protocols/:protocolId"
        element={
          <ProtectedRoute>
            <Layout>
              <ProtocolSetup />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Criteria editor */}
      <Route
        path="/protocols/:protocolId/criteria"
        element={
          <ProtectedRoute>
            <Layout>
              <CriteriaEditor />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Document upload */}
      <Route
        path="/upload"
        element={
          <ProtectedRoute>
            <Layout>
              <DocumentUpload />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Summary review */}
      <Route
        path="/documents/:documentId/summary"
        element={
          <ProtectedRoute>
            <Layout>
              <SummaryReview />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Review results */}
      <Route
        path="/reviews/:reviewId"
        element={
          <ProtectedRoute>
            <Layout>
              <ReviewResults />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Budget (admin only) */}
      <Route
        path="/budget"
        element={
          <ProtectedRoute requireAdmin>
            <Layout>
              <BudgetManagement />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Settings */}
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Layout>
              <MHRSettings />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Multi-Study Consider */}
      <Route
        path="/multi-study"
        element={
          <ProtectedRoute>
            <Layout>
              <MultiStudyConsider />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Catch all - redirect to dashboard */}
      <Route path="*" element={<Navigate to="/mhr" replace />} />
    </Routes>
  );
}

// Main MHR App
export function MHRApp() {
  return (
    <MHRErrorBoundary>
      <MHRRoutes />
    </MHRErrorBoundary>
  );
}

export default MHRApp;
